import React, { FC, useEffect, useState } from "react";
import {
  DataGridPro,
  GridColDef,
  gridVisibleSortedRowIdsSelector,
  GridColumnHeaderParams,
  GridDensity,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { TableProps } from "../types";
import { apiClient } from "api";
import { Box, TablePagination, Tooltip } from "@mui/material";
import TableMenu from "./components/TableMenu";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store/types";
import ExportIcon from "assets/Icons/replenishment/ExportIcon.svg";
import { MUI_TABLE_STYLES, TABLE_PAGINATION_STYLES } from "constants/constants";
import { useQuery } from "react-query";
import { DateFormat, NUMBER_FORMAT } from "utils/utils";
import Alerts from "components/Alerts";
import { addCurrentColumns } from "store/table/table.slice";
import { EXCEL_MEDIA_TYPE } from "./constants";

const columns: GridColDef[] = [
  {
    field: "seller_name",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Seller",
    align: "left",
    headerAlign: "center",
  },
  {
    field: "seller_id",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Seller Id",
    align: "left",
    headerAlign: "center",
  },
  {
    field: "asin",
    filterable: false,
    disableColumnMenu: true,
    headerName: "ASIN",
    headerAlign: "center",
    align: "left",
    minWidth: 150,
  },
  {
    field: "sku",
    filterable: false,
    disableColumnMenu: true,
    headerName: "SKU",
    align: "left",
    headerAlign: "center",
  },
  {
    field: "description",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Description",
    align: "left",
    headerAlign: "center",
  },
  {
    field: "promo_type",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Promo Type",
    align: "left",
    minWidth: 150,
    headerAlign: "center",
  },
  {
    field: "start_date",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Start Date",
    align: "left",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => DateFormat(params?.value),
  },
  {
    field: "end_date",
    filterable: false,
    disableColumnMenu: true,
    headerName: "End Date",
    align: "left",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => DateFormat(params?.value),
  },
  {
    field: "is_overlapping",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Overlapping?",
    align: "left",
    headerAlign: "center",
  },
  {
    field: "discount",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Discount",
    align: "right",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => NUMBER_FORMAT(params),
  },
  {
    field: "budget",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Budget",
    align: "right",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams) => NUMBER_FORMAT(params),
  },
  {
    field: "promotion_id",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Promotion Id",
    align: "left",
    headerAlign: "center",
    minWidth: 150,
  },
  {
    field: "promo_code",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Promo Code",
    align: "left",
    headerAlign: "center",
    minWidth: 150,
  },
  {
    field: "promo_sales_units",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Promo Sales Units",
    align: "right",
    headerAlign: "center",
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => NUMBER_FORMAT(params),
  },
  {
    field: "promo_sales",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Promo Sales ($)",
    align: "right",
    headerAlign: "center",
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => NUMBER_FORMAT(params),
  },
  {
    field: "forecasted_daily_sales",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Forecasted Daily Sales (Total)",
    align: "right",
    headerAlign: "center",
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => NUMBER_FORMAT(params),
  },
  {
    field: "actual_daily_sales",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Actual Daily Sales (Total)",
    align: "right",
    headerAlign: "center",
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => NUMBER_FORMAT(params),
  },
  {
    field: "incremental_daily_sales",
    filterable: false,
    disableColumnMenu: true,
    headerName: "Incremental Daily Sales (Total)",
    align: "right",
    headerAlign: "center",
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => NUMBER_FORMAT(params),
  },
];

const PromotionCalendarTable: FC<TableProps> = ({
  rows,
  isCheckbox,
  fetching,
  getColumnToSort,
  sortType,
  getPageNumber,
  getPageSize,
  newPageNumber,
  newPageSize,
  isGrouping,
  GroupingModel,
  columnsToSort,
}) => {
  const dispatch = useDispatch();
  const density = useSelector(
    (state: AppState) => state.table.density
  ) as GridDensity;
  const filters = useSelector((state: AppState) => state.table.columnFilters);
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);
  const apiRef = useGridApiRef();
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const [downloading, setDownloading] = useState<boolean>(false);
  const [clickDownload, setClickDownload] = useState<boolean>(false);
  const [rowsData, setRowsData] = useState<any[]>([]);

  const totalRecords = useSelector(
    (state: AppState) => state.promotionCalendar.records
  );
  // const [initialRows, setInitialRows] = useState<SalesReportsProps[]>([]);
  const [records, setRecords] = useState<number>(0);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
    if (getPageNumber) getPageNumber(newPage + 1);
  };

  useEffect(() => {
    setRecords(totalRecords);
  }, [totalRecords]);

  useEffect(() => {
    if (
      typeof newPageNumber !== "undefined" &&
      typeof newPageSize !== "undefined"
    ) {
      setPage(newPageNumber);
      setPageSize(newPageSize);
    }
  }, [newPageNumber, newPageSize]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    if (getPageSize) getPageSize(parseInt(event.target.value, 10));
  };

  // useEffect(() => {
  //   if (rows && rows.length) {
  //     setInitialRows(rows);
  //   }
  // }, [rows]);

  useEffect(() => {
    if (filters?.length && filters[1].length) {
      setPage(1);
    }
  }, [filters]);

  useEffect(() => {
    if (columns.length) {
      dispatch(
        addCurrentColumns(
          columns.filter(
            (item: GridColDef) =>
              item.field !== "seller_id" &&
              item.field !== "Action" &&
              item.field !== "uploadDate" &&
              item.field !== "seller_name"
          )
        )
      );
    }
  }, [columns]);

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel) {
        if (sortModel.length) {
          const { field, sort } = sortModel[0];
          if (getColumnToSort && field && sort) getColumnToSort(field, sort);
        }
      }
    },
    []
  );

  const CustomFooter = () => {
    return (
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        component="div"
        sx={TABLE_PAGINATION_STYLES}
        count={records}
        showLastButton
        showFirstButton
        rowsPerPage={pageSize}
        labelDisplayedRows={({ from, to, count, page }) => {
          return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
        }}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    );
  };

  useEffect(() => {
    if (columns.length) {
      dispatch(
        addCurrentColumns(
          columns.filter((item: GridColDef) => item.field !== "seller_name")
        )
      );
    }
  }, [columns]);

  // useEffect(() => {
  //   const currentData = rowsData;
  //   if (filters && filters.length && filters[1].length && currentData.length) {
  //     let arr = Object.keys(
  //       filters?.reduce((value, index) => ({ ...value, [index]: index }), {})
  //     );
  //     if (!isNaN(Number(arr[0]))) arr.reverse();
  //     let temp = arr[1]
  //       .toString()
  //       .split(",")
  //       .map((item: string) => item.trim());
  //     const data: any[] = [];
  //     currentData.filter((item: any, i: number) => {
  //       //@ts-ignore
  //       temp.map((element: string) => {
  //         let compareValue =
  //           currentData[i][arr[0] === "seller_id" ? "SellerId" : arr[0]];
  //         if (compareValue) {
  //           if (!isNaN(Number(compareValue))) {
  //             if (Number(compareValue) == 0 || Number(compareValue) == 0.0)
  //               compareValue = 0;
  //             else compareValue = Number(compareValue).toFixed(1);
  //             element = Number(element).toFixed(1);
  //           } else {
  //             compareValue = compareValue.toString().toLowerCase();
  //             element = element.toLowerCase();
  //           }

  //           if (element == compareValue) {
  //             data.push(item);
  //           }
  //         }
  //       });
  //     });
  //     setFilteredData(data);
  //   } else {
  //     setFilteredData([]);
  //   }
  // }, [filters]);

  // useEffect(() => {
  //   if (data) {
  //     const newData = data?.data.sellers.map((item: any, id: number) => ({
  //       ...item,
  //       id,
  //     }));
  //     setRowsData(newData);
  //   }
  // }, [data]);

  // const handleExport = (options: any) =>
  //   apiRef.current.exportDataAsCsv(options);
  // const getFilteredRows = ({ apiRef }: { apiRef: any }) =>
  //   gridVisibleSortedRowIdsSelector(apiRef);

  return (
    <>
      <TableMenu disableUpload disableExpand widgetName="promotionCalendar" />
      <DataGridPro
        components={{
          Footer: CustomFooter,
        }}
        apiRef={apiRef}
        rows={rows}
        sx={MUI_TABLE_STYLES}
        columns={columns}
        pageSize={pageSize}
        headerHeight={52}
        loading={fetching}
        density={density}
        checkboxSelection={isCheckbox ? true : false}
        pagination
        onSortModelChange={handleSortModelChange}
        sortingMode="server"
        sortingOrder={["asc", "desc"]}
        columnGroupingModel={GroupingModel}
        experimentalFeatures={{
          columnGrouping: isGrouping ? false : true,
          lazyLoading: true,
        }}
      />
      {clickDownload ? (
        <Box
          sx={{
            position: "absolute",
            bottom: "0",
            right: 0,
            cursor: "pointer",
            zIndex: 4,
          }}
        >
          <Alerts
            title={"Download in progress"}
            completeTitle={"Download complete"}
            completeSubTitle={"Restock Summary table has been exported."}
            loading={downloading}
            close={() => setClickDownload(false)}
          />
        </Box>
      ) : (
        <Box
          sx={{
            position: "absolute",
            bottom: "0",
            right: 0,
            cursor: "pointer",
            zIndex: 2,
          }}
          // onClick={() => {
          //   setDownloading(true);
          //   setClickDownload(true);
          //   apiClient
          //     .downloadListingHealth(filters, columnsToSort)
          //     .then((res: any) => {
          //       const link = document.createElement("a");
          //       link.href = EXCEL_MEDIA_TYPE + res;
          //       link.setAttribute(
          //         "download",
          //         `Ridgewood_Listing_Health_${moment(new Date()).format(
          //           "MM_DD_YYYY"
          //         )}.xlsx`
          //       );
          //       document.body.appendChild(link);
          //       link.click();
          //       document.body.removeChild(link);
          //     })
          //     .catch((err: Error) => console.log(err))
          //     .finally(() => {
          //       setDownloading(false);
          //       setTimeout(() => {
          //         setClickDownload(false);
          //       }, 3000);
          //     });
          // }}
        >
          <img src={ExportIcon} alt="export" />
        </Box>
      )}
    </>
  );
};

export default PromotionCalendarTable;
